/**
 * Standing emails (TMMRSL-0046, TMMAPI-0100): every standing email sent to the reseller's owners
 * and admins (`GET /notices`, newest first) — when, which email, what it said, its status in
 * words, and how many people it went to. Recipients are never listed; the server sends a count.
 */
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { resellerBillingApi } from '@/api/resellerBilling';
import { ApiErrorState } from '@/components/ApiErrorState';
import { NumericCell } from '@/components/cells';
import { StatusChip } from '@/components/status/StatusChip';
import { useRemote } from '@/components/useRemote';
import { DATE_ONLY, formatInstantOrDash } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { noticeSummary } from './noticeSummary';

const loadNotices = () => resellerBillingApi.listNotices();

export function StandingEmails() {
  const { t } = useTranslation();
  const locale = useLocale();
  const notices = useRemote(loadNotices);
  const dash = t('statements.unavailable');
  const title = t('agreement.emails.title');

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" component="h2">
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        {t('agreement.emails.subtitle')}
      </Typography>
      {notices.status === 'loading' ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={120} />
        </Box>
      ) : null}
      {notices.status === 'error' ? (
        <ApiErrorState error={notices.error} onRetry={notices.reload} />
      ) : null}
      {notices.status === 'ready' && (notices.data ?? []).length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('agreement.emails.empty')}
        </Typography>
      ) : null}
      {notices.status === 'ready' && notices.data && notices.data.length > 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={title}>
            <TableHead>
              <TableRow>
                <TableCell>{t('agreement.emails.col.date')}</TableCell>
                <TableCell>{t('agreement.emails.col.email')}</TableCell>
                <TableCell>{t('agreement.emails.col.details')}</TableCell>
                <TableCell>{t('agreement.emails.col.status')}</TableCell>
                <TableCell align="right">{t('agreement.emails.col.recipients')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notices.data.map((notice) => (
                <TableRow key={notice.id}>
                  <TableCell component="th" scope="row">
                    {formatInstantOrDash(notice.sentAt ?? notice.createdAt, locale, dash, DATE_ONLY)}
                  </TableCell>
                  <TableCell>{notice.kind ? t(`notices.kind.${notice.kind}`) : dash}</TableCell>
                  <TableCell>{noticeSummary(notice, t, locale)}</TableCell>
                  <TableCell>
                    <StatusChip domain="notice" status={notice.status} />
                  </TableCell>
                  <NumericCell value={notice.recipientCount} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
    </Paper>
  );
}
